import { Switch as AntSwitch, Slider as AntSlider, Tooltip as AntTooltip } from "antd";
import type { ReactNode } from "react";
import { cn } from "@/shared/lib/utils";

interface SwitchProps {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

export function Switch({ checked = false, onCheckedChange, disabled, className }: SwitchProps) {
  return (
    <AntSwitch
      checked={checked}
      onChange={onCheckedChange}
      disabled={disabled}
      className={cn("!bg-black/25 [&.ant-switch-checked]:!bg-[#E4002B]", className)}
     
    />
  );
}

interface ProgressProps {
  className?: string;
  value?: number;
  strokeColor?: string;
}

export function Progress({ className, value = 0, strokeColor }: ProgressProps) {
  const percent = Math.min(100, Math.max(0, value));
  return (
    <div className={cn("h-2 w-full overflow-hidden rounded-full bg-[#F0F0F0]", className)}>
      <div
        className="h-full rounded-full transition-[width] duration-300"
        style={{ width: `${percent}%`, background: strokeColor ?? "#E4002B" }}
      />
    </div>
  );
}

interface SliderProps {
  className?: string;
  value?: number[];
  onValueChange?: (value: number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Renders top-to-bottom (AntD's own `vertical` prop) instead of the
   *  default left-to-right — AntD requires an explicit CSS height for a
   *  vertical slider (it has no intrinsic one, unlike a horizontal slider's
   *  width), passed via `style`. */
  vertical?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
}

export function Slider({ className, value, onValueChange, min = 0, max = 100, step = 1, vertical, style, disabled }: SliderProps) {
  const isRange = Array.isArray(value) && value.length > 1;

  if (isRange) {
    return (
      <AntSlider
        range
        min={min}
        max={max}
        step={step}
        value={value as number[]}
        onChange={(v) => onValueChange?.(v as number[])}
        vertical={vertical}
        style={style}
        disabled={disabled}
        className={cn("[&_.ant-slider-track]:!bg-[#E4002B] [&_.ant-slider-handle]:!border-[#E4002B]", className)}
      />
    );
  }

  return (
    <AntSlider
      min={min}
      max={max}
      step={step}
      value={value?.[0] ?? min}
      onChange={(v) => onValueChange?.([v as number])}
      vertical={vertical}
      style={style}
      disabled={disabled}
      className={cn("[&_.ant-slider-track]:!bg-[#E4002B] [&_.ant-slider-handle]:!border-[#E4002B]", className)}
    />
  );
}

export function Tooltip({
  title,
  placement = "top",
  overlayClassName,
  children,
}: {
  title: ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
  overlayClassName?: string;
  children: ReactNode;
}) {
  return (
    <AntTooltip
      title={title}
      placement={placement}
      color="#3A3D42"
      classNames={{ container: overlayClassName }}
    >
      {children}
    </AntTooltip>
  );
}